import type { WorkflowDefinition } from "../types";

/**
 * W13 — Optimize a draft press release for AI citation.
 *
 * `perTarget` on the draft's pipeline run: a comms team has several releases
 * in flight at once, and nobody should be blocked because a colleague is
 * mid-revision on another one. The runner's CTA carries `?workflowRun=<runId>`
 * and the optimizer binds the run it creates.
 *
 * Every `since` is `scope: "target"`, so a colleague's draft never checks off
 * this run's steps and the steps stay open until this run names its draft.
 */
export const pressReleaseWorkflow: WorkflowDefinition = {
	id: "press-release",
	wNumber: 13,
	title: "Optimize a press release for AI citation before it goes out",
	outcome: "Fix the release before it goes to the wire, not after.",
	audiences: ["pr"],
	uses: ["Citation Optimizer", "Prompts"],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "pipelineRun", label: "Draft", pipelineMode: "draft" },
	// Mirrors the Citation Optimizer route itself, which needs a content anchor
	// to score against: a BRAND's crawlable domain or a PRODUCT's seed URL.
	// PERSON, CATEGORY and LOCATION have neither, and that page 404s for them.
	isApplicable: (mode) => mode.isBrand || mode.isProduct,
	articleSlug: "how-to-optimize-a-press-release-for-ai-citation",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "paste-draft",
			title: "Paste the draft against a real search",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've scored a draft for this workflow.",
			why: 'Name the search the release should win before you score anything. A release built around "we have news" chases nothing.',
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Pick the tracked search the release should win",
				"Paste the draft straight from your team's document",
				"Set the page type to press release",
			],
			complete: {
				kind: "all",
				of: [
					{
						kind: "since",
						event: "pipelineRunCreated",
						scope: "target",
					},
					{ kind: "count", key: "pipeline.draft", gte: 1 },
				],
			},
		},
		{
			id: "score",
			title: "Score it gate by gate before the wire",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've completed a score for this workflow.",
			why: "Five checks decide whether AI can find your release, read it and quote it. Tight comms copy can still fail any of them.",
			docsHref: "/docs/methodology/citation-optimizer",
			tasks: [
				"Score it and read the stoplight strip, not one number",
				"Open the failing gates for specific advice",
				"Score even when the draft looks ready, because it is free",
			],
			complete: {
				kind: "since",
				event: "pipelineRunCompleted",
				scope: "target",
			},
			spend: {
				kind: "free",
				note: "Scoring and re-scoring are free. Only a rewrite costs credits.",
			},
			signal: ({ state }) => {
				const score =
					state.target?.pipeline?.latestScore ??
					state.property?.pipeline.latestScore ??
					null;
				if (score === null) {
					return null;
				}
				return {
					kind: "score",
					label: "Citation score",
					value: score,
					target: 70,
				};
			},
		},
		{
			id: "revise",
			title: "Fix what failed without breaking the format",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've generated a rewrite for this workflow.",
			why: "Fix the checks that failed, not the whole release. Quotes and boilerplate were signed off separately, so read those changes twice.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Generate the rewrite and read the diff against the failing gates",
				"Leave quotes, attribution and boilerplate alone",
				"Score again for free until the release is ready",
			],
			complete: {
				kind: "since",
				event: "pipelineRevisionCreated",
				scope: "target",
			},
			spend: { kind: "citationRewrite" },
			signal: ({ state }) => {
				const revisions = state.target?.pipeline?.revisionCount ?? null;
				if (revisions === null) {
					return null;
				}
				return {
					kind: "count",
					label: "Rewrites generated",
					value: revisions,
					target: 1,
				};
			},
		},
		{
			id: "export",
			title: "Export it and clear it for the wire",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've cleared the release for the wire.",
			why: "Treat the readiness banner like legal review, as a hard gate. One asks whether AI can cite it, the other asks what it says.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Export the markdown or HTML plus title, meta and structured data",
				"Hand the package to whoever sends it to the wire",
				"Log the wire pickup as a coverage group afterwards",
			],
			complete: { kind: "manual" },
		},
	],
};
